import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Zoom
} from '@mui/material';
import {
  EmojiEvents,
  TrendingUp,
  Whatshot,
  Star
} from '@mui/icons-material';
import BadgeDisplay from './BadgeDisplay';
import { UserTitles } from './UserTitle';
import { useAchievements } from '../../hooks/useAchievements';
import { useNavigate } from 'react-router-dom';
import BadgedAvatar from '../Common/BadgedAvatar';

const LEADERBOARD_TYPES = {
  HYPE_HUNTERS: {
    title: 'Top Hype Hunters',
    icon: Whatshot,
    color: '#FF5722',
    description: 'Users who discover trending content first'
  },
  TASTE_MAKERS: {
    title: 'Top Taste Makers',
    icon: Star,
    color: '#FFD700',
    description: 'Users with the best recommendations'
  },
  REVIEWERS: {
    title: 'Top Reviewers',
    icon: EmojiEvents,
    color: '#4CAF50',
    description: 'Most helpful and active reviewers'
  },
  INFLUENCERS: {
    title: 'Top Influencers',
    icon: Star,
    color: '#9C27B0',
    description: 'Users with highest follower engagement'
  }
};

const RANK_COLORS = {
  1: '#FFD700', // Gold
  2: '#C0C0C0', // Silver
  3: '#CD7F32'  // Bronze
};

const LeaderboardCard = ({ type, achievementType = null, timeframe = 'ALL_TIME', showRank = true, maxUsers = 5 }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { getLeaderboard } = useAchievements();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const leaderboard = LEADERBOARD_TYPES[type];
  const IconComponent = leaderboard?.icon;
  
  useEffect(() => {
    if (!leaderboard) return;
    
    const loadLeaderboard = async () => {
      setLoading(true);
      try {
        const data = await getLeaderboard(achievementType, timeframe, maxUsers);
        setUsers(data || []);
      } catch (error) {
        console.error('Error loading leaderboard:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadLeaderboard();
  }, [leaderboard, achievementType, timeframe, maxUsers]);
  
  if (!leaderboard) return null;
  
  const displayUsers = users.slice(0, maxUsers);

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getRankGradient = (rank) => {
    switch (rank) {
      case 1: return 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)';
      case 2: return 'linear-gradient(135deg, #C0C0C0 0%, #9E9E9E 100%)';
      case 3: return 'linear-gradient(135deg, #CD7F32 0%, #8D5524 100%)';
      default: return 'none';
    }
  };

  const handleUserClick = (user) => {
    if (user.username) {
      navigate(`/profile/${user.username}`);
    }
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar
            sx={{
              bgcolor: leaderboard.color,
              width: 32,
              height: 32,
              mr: 2
            }}
          >
            <IconComponent sx={{ fontSize: 18 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {leaderboard.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {leaderboard.description}
            </Typography>
          </Box>
        </Box>

        {/* User List */}
        <List dense>
          {displayUsers.map((user, index) => {
            const rank = index + 1;
            const rankColor = RANK_COLORS[rank] || 'text.secondary';

            return (
              <Zoom in={true} timeout={200 + index * 50} key={user.id || user.userId}>
                <ListItem
                  onClick={() => handleUserClick(user)}
                  sx={{
                    px: 0,
                    py: 1,
                    borderRadius: 2,
                    mb: 0.5,
                    cursor: 'pointer',
                    background: rank <= 3 ? getRankGradient(rank) : 'transparent',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      bgcolor: rank <= 3 ? 'rgba(255, 255, 255, 0.1)' : 'action.hover',
                      transform: 'translateY(-1px)',
                      boxShadow: rank <= 3 
                        ? `0 4px 15px rgba(${rank === 1 ? '255, 215, 0' : rank === 2 ? '192, 192, 192' : '205, 127, 50'}, 0.3)`
                        : theme.shadows[2]
                    },
                    transition: 'all 0.2s ease',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center'
                  }}
                >
                  {showRank && (
                    <Box
                      sx={{
                        minWidth: isMobile ? 'auto' : 40,
                        textAlign: 'center',
                        mr: isMobile ? 0 : 1,
                        mb: isMobile ? 0.5 : 0,
                        order: isMobile ? -1 : 'initial'
                      }}
                    >
                      <Chip
                        label={getRankIcon(rank)}
                        size="small"
                        sx={{
                          bgcolor: rankColor,
                          color: rank <= 3 ? 'black' : 'white',
                          fontWeight: 700,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>
                  )}

                  <ListItemAvatar sx={{ mr: isMobile ? 0 : 1 }}>
                    <BadgedAvatar
                      user={{
                        ...user,
                        displayName: user.displayName || user.username,
                        avatar: user.avatar,
                        activeBadges: user.activeBadges || []
                      }}
                      badges={user.badges || []}
                      size={isMobile ? 36 : 40}
                      sx={{
                        border: rank <= 3 ? '2px solid rgba(255,255,255,0.5)' : '1px solid transparent'
                      }}
                    />
                  </ListItemAvatar>

                  <ListItemText
                    sx={{ flex: 1, minWidth: 0 }}
                    primary={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography 
                          variant={isMobile ? "caption" : "subtitle2"} 
                          fontWeight={600}
                          sx={{ 
                            color: rank <= 3 ? 'white' : 'text.primary',
                            textShadow: rank <= 3 ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none'
                          }}
                        >
                          {user.displayName || user.username}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        {user.title && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: rank <= 3 ? 'rgba(255,255,255,0.9)' : 'primary.main',
                              fontWeight: 500,
                              display: 'block'
                            }}
                          >
                            {user.title}
                          </Typography>
                        )}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: rank <= 3 ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                            display: 'block'
                          }}
                        >
                          {user.totalPoints || 0} points
                        </Typography>
                      </Box>
                    }
                  />

                  <Box textAlign="right">
                    <Chip
                      label={user.achievementCount || '0'}
                      size="small"
                      sx={{
                        bgcolor: rank <= 3 ? 'rgba(255,255,255,0.2)' : `${leaderboard.color}20`,
                        color: rank <= 3 ? 'white' : leaderboard.color,
                        fontWeight: 600,
                        border: rank <= 3 ? '1px solid rgba(255,255,255,0.3)' : 'none'
                      }}
                    />
                  </Box>
                </ListItem>
              </Zoom>
            );
          })}
        </List>

        {loading && (
          <Box textAlign="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        )}
        
        {!loading && users.length === 0 && (
          <Box textAlign="center" py={3}>
            <Typography variant="body2" color="text.secondary">
              No data available yet
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default LeaderboardCard;